import React from "react";
import { useAnimationTimer } from "hooks/useAnimationTimer";
import "./triangleProgress.scss";

function easeInOutCubic(x: number): number {
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

let getHeightFromSide = (side) => side * 0.8660254038;

let makeEmptySideFromValue = (side, value) =>
  Math.sqrt((1 - value) * side * side);

let makeBasePath = (origin, side, value) => {
  const height = getHeightFromSide(side),
    emptySide = makeEmptySideFromValue(side, value),
    emptyHeight = getHeightFromSide(emptySide);
  return `
    M${origin[0] - side / 2} ${origin[1] + height}
    H${origin[0] + side / 2}
    L${origin[0] + emptySide / 2} ${origin[1] + emptyHeight}
    H${origin[0] - emptySide / 2}
    L${origin[0] - side / 2} ${origin[1] + height}Z
  `;
};

let makeTrianglePath = (origin, side, value) => {
  const emptySide = makeEmptySideFromValue(side, value);
  return `
    M${origin[0]} ${origin[1]}
    L${origin[0] - emptySide / 2} ${origin[1] + getHeightFromSide(emptySide)}
    H${origin[0] + emptySide / 2}
    L${origin[0]} ${origin[1]}Z
  `;
};

function TriangleProgress({
  value,
  animSteps = [{ start: 0, end: 1, from: value, to: value }],
}) {
  const duration = animSteps[animSteps.length - 1].end;
  const animationTime = useAnimationTimer(duration, 0);
  const currentStep = animSteps.find(
    (step) => step.start <= animationTime && step.end >= animationTime
  );

  const { start, end, from, to } = currentStep;

  const animValue =
    easeInOutCubic((animationTime - start) / (end - start)) * (to - from) +
    from;

  const origin = [107, 0];
  const side = 173;

  return (
    <svg
      className="triangle-progress"
      viewBox="0 0 214 151"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g filter="url(#filter0_d)">
        <path
          className="animate"
          d={makeBasePath(origin, side, animValue)}
          fill="url(#paint0_linear)"
        />
      </g>
      <path
        className="animate"
        d={makeTrianglePath(origin, side, animValue)}
        fill="url(#paint1_linear)"
      />
      <defs>
        <filter
          id="filter0_d"
          x="0"
          y="0"
          width="213.205"
          height="150"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="10" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.6375 0 0 0 0 0.0823438 0 0 0 0 0.628893 0 0 0 1 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
        <linearGradient
          id="paint0_linear"
          x1="107"
          y1="0"
          x2="107"
          y2="150"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FF9A75" />
          <stop offset="1" stopColor="#F427AF" />
        </linearGradient>
        <linearGradient
          id="paint1_linear"
          x1="107"
          y1="0"
          x2="107"
          y2="150"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FDFEFF" />
          <stop offset="1" stopColor="#EFF0FF" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export { TriangleProgress };
